// =============================================================================
// FILE: packages/renderer/src/components/public/CivilEstimator.tsx
// PURPOSE: "Civil Ductbank & Manhole Estimator" (A1) + auto-detailing (A2).
//   Route segments (underground / bridge / indoor) from coordinates → length,
//   auto-manholes, Indoor/Outdoor BOM split, and a $99 certified multi-page
//   package (route profile + manhole detail + BOM). Includes a Government
//   "Train Mode" rough rail/transit bidding preset. Used as a public landing
//   portal and as an in-app tool.
// =============================================================================

import { useMemo, useState, type JSX } from 'react';
import { X, Route as RouteIcon, Plus, Trash2, Sparkles, CreditCard, Download, Check, TrainFront } from 'lucide-react';
import {
  computeCivil, generateCivilPackage,
  type RouteSegment, type SegKind, type CivilPackageResult,
} from '../../lib/civilEstimatorGenerator.js';
import { proofread } from '../../lib/technicalProofreader.js';

const backendUrl = (import.meta.env.VITE_BACKEND_URL as string) ?? '';
const PACKAGE_FEE = 99;
const teal = '#25B5A8';
const usd = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const SEED: RouteSegment[] = [
  { id: 's1', name: 'Street ductbank run',  kind: 'underground', origin: 'Calgary', destination: '51.05,-114.06' },
  { id: 's2', name: 'River bridge crossing', kind: 'bridge',      origin: '51.05,-114.06', destination: '51.06,-114.05' },
  { id: 's3', name: 'Building entrance',     kind: 'indoor',      origin: '51.06,-114.05', destination: '51.061,-114.049' },
];
const TRAIN_SEED: RouteSegment[] = [
  { id: 't1', name: 'Transit corridor (main line)', kind: 'underground', origin: 'Calgary', destination: 'Edmonton' },
];

const LABOR = [{ label: 'Standard', mult: 1.0 }, { label: 'Union', mult: 1.35 }, { label: 'Prevailing', mult: 1.6 }];

export function CivilEstimator({ onClose, embedded = false }: { onClose?: () => void; embedded?: boolean }): JSX.Element {
  const [trainMode, setTrainMode] = useState(false);
  const [segments, setSegments] = useState<RouteSegment[]>(SEED);
  const [laborLabel, setLaborLabel] = useState('Standard');
  const [laborMult, setLaborMult] = useState(1.0);
  const [markupPct, setMarkupPct] = useState(15);
  const [project, setProject] = useState('Sierra Manor — Civil');
  const [calc, setCalc] = useState(false);
  const [paid, setPaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payMsg, setPayMsg] = useState<string | null>(null);
  const [pkg, setPkg] = useState<CivilPackageResult | null>(null);
  const [polishMsg, setPolishMsg] = useState<string | null>(null);

  const result = useMemo(() => computeCivil(segments, laborMult, markupPct), [segments, laborMult, markupPct]);

  const toggleTrain = () => {
    const next = !trainMode; setTrainMode(next);
    setSegments(next ? TRAIN_SEED : SEED); setCalc(false); setPaid(false); setPkg(null);
  };
  const addSeg = () => setSegments((c) => [...c, { id: `s${Date.now()}`, name: 'New segment', kind: 'underground', origin: 'Calgary', destination: 'Toronto' }]);
  const upd = (id: string, p: Partial<RouteSegment>) => setSegments((c) => c.map((s) => (s.id === id ? { ...s, ...p } : s)));
  const rm = (id: string) => setSegments((c) => c.filter((s) => s.id !== id));

  const emmy = calc
    ? `I mapped a ${Math.round(result.totalFt).toLocaleString()} ft route — ${Math.round(result.outdoorFt).toLocaleString()} ft underground, ${Math.round(result.bridgeFt).toLocaleString()} ft bridge, ${Math.round(result.indoorFt).toLocaleString()} ft indoor. That needs ${result.manholes} manholes. Rough construction estimate: ${usd(result.total)} USD.`
    : '';

  const buy = async () => {
    setBusy(true); setPayMsg('Opening secure PayPal checkout ($99)…');
    try {
      const res = await fetch(`${backendUrl}/api/pricing/deposit-order`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: `civil-${Date.now()}`, email: 'client@bimops.local', amount: PACKAGE_FEE.toFixed(2), currency: 'USD', description: 'Certified Civil Infrastructure Package' }),
      });
      if (res.ok) { const d = await res.json() as { approval_url?: string }; if (d.approval_url) window.open(d.approval_url, '_blank', 'noopener'); setPayMsg('Checkout opened in a new tab. Your package is unlocked below.'); }
      else setPayMsg('PayPal unavailable — unlocking your package for this session.');
    } catch { setPayMsg('Network issue reaching PayPal — unlocking your package for this session.'); }
    // Technical Proofreader — final Auto-Polish pass before PDF generation.
    const polished = proofread(project);
    if (polished.text !== project) setProject(polished.text);
    setPolishMsg(polished.corrections.length > 0
      ? `Technical Proofreader: auto-polished ${polished.corrections.length} issue(s) · acronyms preserved.`
      : 'Technical Proofreader: text clean · acronyms preserved.');
    const g = await generateCivilPackage(result, polished.text); setPkg(g); setPaid(true); setBusy(false);
  };
  const download = () => { if (!pkg) return; const a = document.createElement('a'); a.href = pkg.blobUrl; a.download = pkg.fileName; a.click(); };

  const body = (
    <div className="p-6">
      {/* header row */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <RouteIcon className="h-5 w-5" style={{ color: teal }} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: `${teal}cc` }}>Infrastructure & Compliance</div>
            <div className="text-base font-bold text-zinc-100">Civil Ductbank & Manhole Estimator</div>
          </div>
        </div>
        <button type="button" data-testid="ce-train-mode" onClick={toggleTrain}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-sm text-[11px] uppercase tracking-[0.14em]"
          style={{ border: `1px solid ${trainMode ? '#818CF8' : 'rgba(120,140,170,0.35)'}`, background: trainMode ? 'rgba(129,140,248,0.15)' : 'rgba(30,41,59,0.4)', color: trainMode ? '#a5b4fc' : '#94a3b8' }}>
          <TrainFront className="h-3.5 w-3.5" /> Government Train Mode {trainMode ? '· On' : ''}
        </button>
      </div>

      <input data-testid="ce-project" value={project} onChange={(e) => setProject(e.target.value)}
        className="w-full h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] text-zinc-100 mb-4 focus:outline-none focus:border-teal-400/60" placeholder="Project name" />

      {trainMode && (
        <div className="mb-3 text-[12px] text-indigo-300 rounded-sm px-3 py-2" style={{ background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.3)' }}>
          Rail/Transit rough bid — minimal inputs, high-level corridor estimate.
        </div>
      )}

      {/* segments */}
      <div className="space-y-2 mb-4" data-testid="ce-segments">
        {segments.map((s, i) => (
          <div key={s.id} data-testid={`ce-seg-${i}`} className="grid grid-cols-12 gap-2 items-center rounded-lg p-2" style={{ background: 'rgba(12,20,40,0.55)', border: '1px solid rgba(63,63,70,0.5)' }}>
            <input className="col-span-3 h-9 bg-zinc-900/60 border border-zinc-800 rounded-sm px-2 text-[12px] text-zinc-100" value={s.name} onChange={(e) => upd(s.id, { name: e.target.value })} />
            <select data-testid={`ce-seg-kind-${i}`} className="col-span-2 h-9 bg-zinc-900/60 border border-zinc-800 rounded-sm px-1 text-[12px] text-zinc-100" value={s.kind} onChange={(e) => upd(s.id, { kind: e.target.value as SegKind })}>
              <option value="underground">Underground</option><option value="bridge">Bridge</option><option value="indoor">Indoor</option>
            </select>
            <input className="col-span-3 h-9 bg-zinc-900/60 border border-zinc-800 rounded-sm px-2 text-[12px] text-zinc-100" value={s.origin} onChange={(e) => upd(s.id, { origin: e.target.value })} placeholder="From (addr / lat,lng)" />
            <input className="col-span-3 h-9 bg-zinc-900/60 border border-zinc-800 rounded-sm px-2 text-[12px] text-zinc-100" value={s.destination} onChange={(e) => upd(s.id, { destination: e.target.value })} placeholder="To (addr / lat,lng)" />
            <button type="button" data-testid={`ce-seg-remove-${i}`} onClick={() => rm(s.id)} className="col-span-1 grid place-items-center text-zinc-600 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <button type="button" data-testid="ce-add-segment" onClick={addSeg} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] rounded-sm border border-teal-500/40 bg-teal-500/10 text-teal-300"><Plus className="h-3.5 w-3.5" /> Add segment</button>
        <div className="flex items-center gap-2">
          {LABOR.map((p) => (
            <button key={p.label} type="button" data-testid={`ce-labor-${p.label.toLowerCase()}`} onClick={() => { setLaborLabel(p.label); setLaborMult(p.mult); }}
              className="px-2.5 py-1.5 rounded-sm text-[11px]" style={{ background: laborLabel === p.label ? `${teal}33` : 'rgba(30,41,59,0.5)', color: laborLabel === p.label ? '#5eead4' : '#94a3b8', border: `1px solid ${laborLabel === p.label ? teal + '77' : 'rgba(63,63,70,0.6)'}` }}>{p.label} {p.mult.toFixed(2)}×</button>
          ))}
          <label className="text-[11px] text-zinc-400 flex items-center gap-2">Markup <span className="text-teal-400" data-testid="ce-markup-value">{markupPct}%</span>
            <input type="range" min={0} max={40} value={markupPct} data-testid="ce-markup-slider" onChange={(e) => setMarkupPct(Number(e.target.value))} className="accent-teal-500 w-24" />
          </label>
        </div>
      </div>

      <button type="button" data-testid="ce-calculate" onClick={() => setCalc(true)}
        className="w-full h-11 rounded-lg font-bold text-[13px] uppercase tracking-[0.12em] flex items-center justify-center gap-2" style={{ background: teal, color: '#04110f' }}>
        <RouteIcon className="h-4 w-4" /> Calculate Route & Auto-Draft
      </button>

      {calc && (
        <div className="mt-6" data-testid="ce-result">
          <div className="flex items-start gap-3 mb-4 rounded-xl p-3" style={{ background: `${teal}0f`, border: `1px solid ${teal}2b` }}>
            <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: teal }} />
            <p className="text-[12.5px] text-zinc-200 leading-relaxed m-0" data-testid="ce-emmy-msg"><span className="font-semibold" style={{ color: teal }}>R²:</span> {emmy}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {[['Total', `${Math.round(result.totalFt).toLocaleString()} ft`], ['Underground', `${Math.round(result.outdoorFt).toLocaleString()} ft`], ['Bridge', `${Math.round(result.bridgeFt).toLocaleString()} ft`], ['Indoor', `${Math.round(result.indoorFt).toLocaleString()} ft`], ['Manholes', String(result.manholes)]].map(([k, v]) => (
              <div key={k} className="rounded-lg p-2 text-center" style={{ background: 'rgba(12,20,40,0.6)', border: '1px solid rgba(63,63,70,0.5)' }}>
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">{k}</div><div className="text-[15px] font-bold text-teal-300">{v}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl p-4 mb-4 text-center" style={{ background: `${teal}12`, border: `1px solid ${teal}44` }}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">Rough Construction Estimate</div>
            <div className="text-3xl font-extrabold" style={{ color: teal }} data-testid="ce-total">{usd(result.total)} <span className="text-base text-zinc-500 font-semibold">USD</span></div>
            <div className="text-[11px] text-zinc-500 mt-1">Materials {usd(result.materials)} · Labor {usd(result.labor)} · Markup {usd(result.markup)}</div>
          </div>

          {!paid ? (
            <div className="rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" style={{ background: 'rgba(252,211,77,0.06)', border: '1px solid rgba(252,211,77,0.28)' }}>
              <div><div className="text-[13px] font-bold text-amber-200">Certified Civil Package · $99</div><div className="text-[11px] text-zinc-400">Route profile + manhole detail + Indoor/Outdoor BOM (3-page PDF)</div></div>
              <button type="button" data-testid="ce-buy-btn" onClick={buy} disabled={busy} className="h-11 px-5 rounded-lg font-bold text-[12px] uppercase tracking-[0.1em] flex items-center gap-2 disabled:opacity-50" style={{ background: teal, color: '#04110f' }}><CreditCard className="h-4 w-4" /> {busy ? 'Processing…' : 'Buy & Download'}</button>
            </div>
          ) : (
            <div className="rounded-xl p-4" style={{ background: `${teal}12`, border: `1px solid ${teal}44` }}>
              <div className="flex items-center gap-2 mb-3 text-[13px]" style={{ color: '#6ee7b7' }} data-testid="ce-paid-confirm"><Check className="h-4 w-4" /> Package unlocked.</div>
              <button type="button" data-testid="ce-download-btn" onClick={download} className="w-full h-11 rounded-lg font-bold text-[13px] uppercase tracking-[0.12em] flex items-center justify-center gap-2" style={{ background: teal, color: '#04110f' }}><Download className="h-4 w-4" /> Download Civil Package PDF</button>
              {polishMsg && <p className="text-[11px] text-teal-300/80 mt-2" data-testid="ce-polish-msg">{polishMsg}</p>}
            </div>
          )}
          {payMsg && <p className="text-[12px] text-zinc-400 mt-2" data-testid="ce-pay-msg">{payMsg}</p>}
        </div>
      )}
    </div>
  );

  if (embedded) return <div data-testid="civil-estimator-embedded" className="rounded-2xl" style={{ background: 'rgba(9,14,24,0.6)', border: `1px solid ${teal}33` }}>{body}</div>;

  return (
    <div data-testid="civil-estimator-modal" className="fixed inset-0 z-[10000] grid place-items-center p-4 overflow-y-auto" style={{ background: 'rgba(4,7,14,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-4xl rounded-2xl my-6" style={{ background: 'linear-gradient(160deg,#0b1220,#0a0f1a)', border: `1px solid ${teal}44`, boxShadow: '0 30px 120px rgba(0,0,0,0.7)' }}>
        <button type="button" data-testid="ce-close" onClick={onClose} className="absolute right-4 top-4 h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200 z-10" style={{ border: '1px solid rgba(63,63,70,0.6)' }}><X className="h-4 w-4" /></button>
        {body}
      </div>
    </div>
  );
}
